import { useCartStore } from "@/store/cart";
import { useUpselledStore } from "@/store/upselled";
import Shipping from "@/services/shipping";
import { useDraftStore } from "@/store/draft";
import CouponService from "@/services/coupon";
import Logger from "@/services/logger";
import Utils from "@/services/utils";
import PriceDisplay from "@/services/priceDisplay";

export default class CartService {
  static addToCartLoading(loading) {
    if (loading) {
      // starts loading
      useCartStore().updateCartLoading(loading);
    } else {
      // ends loading
      useCartStore().updateCartLoading(loading);
      document.dispatchEvent(new Event("vue-app-product-added-to-cart"));
    }
  }

  static updateCartLoading(loading) {
    if (loading) {
      // starts loading
      useCartStore().updateCartLoading(loading);
    } else {
      // ends loading
      useCartStore().updateCartLoading(loading);
    }
  }

  static addToCart(itemToAdd, options = {}) {
    this.addToCartLoading(true);

    //We need at least defaultPrice or startingPrice to add item to cart
    if (
      !itemToAdd.qtyIncr ||
      !itemToAdd.name ||
      !itemToAdd.choice.id ||
      !itemToAdd.choice.name ||
      !itemToAdd.choice?.hasOwnProperty("unitPrice") ||
      (!itemToAdd.choice?.hasOwnProperty("defaultPrice") &&
        !itemToAdd.choice?.hasOwnProperty("startingPrice"))
    ) {
      console.error("missing data to add item to cart");
      console.log(itemToAdd);
      return;
    }

    useCartStore().cart.shippingCountry =
      useDraftStore().selectedCountryCode ||
      window.globalConfig.defaultCountryCode ||
      "FRA";

    // Check if this is a postUpsell item (from confirmation page)
    const isPostUpsell = itemToAdd.isPostUpsell || false;

    // Check for existing items in cart (both regular items and post-upsells)
    const existingItemInCart = useCartStore().cart.items.find(
      (item) =>
        item.choice.id === itemToAdd.choice.id &&
        item.isFromCoupon === itemToAdd.isFromCoupon &&
        Boolean(item.isQuickSellStack) ===
          Boolean(itemToAdd.isQuickSellStack) &&
        String(item.quickSellParentChoiceId || "") ===
          String(itemToAdd.quickSellParentChoiceId || "") &&
        // For post-upsells, also check that both are post-upsells
        (isPostUpsell ? item.isPostUpsell : !item.isPostUpsell),
    );

    if (
      existingItemInCart &&
      (window.globalConfig.shopAllowQtyModification || isPostUpsell) &&
      !itemToAdd.isFromCoupon
    ) {
      existingItemInCart.qty += itemToAdd.qtyIncr;
      document.dispatchEvent(
        new CustomEvent("cart-updated-campaign-stats", {
          detail: {
            eventType: "cart_item_upgrade",
            eventDetails: existingItemInCart,
          },
        }),
      );
      this.addToCartLoading(false);
      return;
    } else if (
      existingItemInCart &&
      !window.globalConfig.shopAllowQtyModification &&
      !isPostUpsell
    ) {
      // Only remove existing items for non-post-upsells when qty modification is disabled
      const existingItemIndex =
        useCartStore().cart.items.indexOf(existingItemInCart);
      useCartStore().cart.items.splice(existingItemIndex, 1); // removing existing item
    }

    // Add the item to cart (for both regular items and postUpsells)
    const cleanItemToAdd = JSON.parse(JSON.stringify(itemToAdd)); // deep copy
    cleanItemToAdd.qty = itemToAdd.qtyIncr;
    delete cleanItemToAdd.qtyIncr;

    console.log("addToCart", itemToAdd);
    Logger.log("AddToCart", {
      product: itemToAdd.name,
      choice: itemToAdd.choice.name,
      qty: cleanItemToAdd.qty,
    });

    if (!cleanItemToAdd.externalOrderLineId) {
      cleanItemToAdd.externalOrderLineId = Utils.generateUID();
    }

    useCartStore().cart.items.push(cleanItemToAdd);

    document.dispatchEvent(
      new CustomEvent("cart-updated-campaign-stats", {
        detail: { eventType: "cart_item_add", eventDetails: cleanItemToAdd },
      }),
    );

    this.flattenGifts();

    this.updateCart();

    if (!options.skipUrlCoupons && !itemToAdd.isFromCoupon) {
      CouponService.applyCouponsFromUrl();
    }

    this.addToCartLoading(false);
  }

  static updateCart() {
    CartService.updateCartLoading(true);

    this.recalculateCart();
    useCartStore().saveCart();

    document.dispatchEvent(
      new CustomEvent("cart-updated", { detail: useCartStore().cart }),
    );
    CartService.updateCartLoading(false);
  }

  static recalculateCart() {
    const cart = useCartStore().cart;

    let defaultSubtotal = 0;
    let subtotal = 0;

    for (const item of cart.items) {
      item.defaultSubtotal = this.calculateDefaultSubtotal(item);
      item.subtotal = item.qty * item.choice.unitPrice;
    }

    cart.items = cart.items.filter((item) => item.qty !== 0);

    for (const item of cart.items) {
      subtotal += item.subtotal;
      defaultSubtotal += item.defaultSubtotal;
    }
    if (cart.items.length === 0) {
      cart.coupons = {}; // empty coupon if empty cart
    }

    cart.defaultSubtotal = defaultSubtotal;
    cart.subtotal = subtotal;

    cart.shippingPrice = this.getShippingPriceTotal();
    cart.freeShippingProgression = Shipping.freeShippingProgression(
      useCartStore().cart.items,
    );
    cart.total = cart.subtotal + cart.shippingPrice;

    if (cart.coupons && Object.keys(cart.coupons).length) {
      CouponService.checkCoupons(this.minifyCart()); // async
    }

    return cart;
  }

  static deleteItem(itemToDelete) {
    Logger.log("deleteItem", {
      product: itemToDelete.name,
    });

    CartService.updateCartLoading(true);

    const items = useCartStore().cart.items;
    const existingItemInCart = items.find(
      (item) =>
        item.choice.id === itemToDelete.choice.id &&
        item.isFromCoupon === itemToDelete.isFromCoupon &&
        Boolean(item.isQuickSellStack) ===
          Boolean(itemToDelete.isQuickSellStack) &&
        String(item.quickSellParentChoiceId || "") ===
          String(itemToDelete.quickSellParentChoiceId || ""),
    );
    if (existingItemInCart) {
      existingItemInCart.qty = 0;
    }

    if (!itemToDelete?.isQuickSellStack) {
      const parentChoiceId = String(itemToDelete?.choice?.id || "");
      for (const item of items) {
        if (
          item.isQuickSellStack &&
          String(item.quickSellParentChoiceId || "") === parentChoiceId
        ) {
          item.qty = 0;
        }
      }
    }

    const remainingParentIds = new Set(
      items
        .filter((item) => item.qty > 0 && !item.isQuickSellStack)
        .map((item) => String(item.choice?.id || "")),
    );
    for (const item of items) {
      if (!item.isQuickSellStack || item.qty === 0) {
        continue;
      }
      const linkedParent = String(item.quickSellParentChoiceId || "");
      if (linkedParent && !remainingParentIds.has(linkedParent)) {
        item.qty = 0;
      } else if (!linkedParent && remainingParentIds.size === 0) {
        item.qty = 0;
      }
    }

    this.updateCart();

    CartService.updateCartLoading(false);
    document.dispatchEvent(
      new CustomEvent("cart-updated-campaign-stats", {
        detail: { eventType: "cart_item_delete", eventDetails: itemToDelete },
      }),
    );
  }

  static emptyCart() {
    Logger.log("emptyCart");

    useCartStore().updateCart({ items: [] });
    useUpselledStore().updateUpselled({});
    this.updateCart();
  }

  static getShippingPriceTotal() {
    const countryCode =
      useDraftStore().selectedCountryCode ||
      window.globalConfig.defaultCountryCode ||
      "FRA";

    return Shipping.getShippingPriceTotal(
      useCartStore().cart.items,
      countryCode,
    );
  }

  static flattenGifts(cart = null) {
    const targetCart = cart || useCartStore().cart;

    if (!targetCart?.items) {
      return;
    }

    for (const item of targetCart.items) {
      if (!item.gifts?.length) {
        item.gifts = CartService.resolveGiftsForCartItem(item);
      }
    }
  }

  static resolveGiftsForCartItem(item, productDetails = window.productDetails) {
    if (item?.gifts?.length) {
      return item.gifts;
    }

    const choiceId = item?.choice?.id;
    if (!choiceId) {
      return [];
    }

    const choicesGifts =
      item?.choicesGifts?.length > 0
        ? item.choicesGifts
        : productDetails?.choicesGifts;
    const giftsDetails =
      item?.giftsDetails?.length > 0
        ? item.giftsDetails
        : productDetails?.giftsDetails;

    if (!choicesGifts?.length || !giftsDetails?.length) {
      return [];
    }

    const gifts = [];

    for (const giftItem of choicesGifts) {
      if (giftItem.parentId !== choiceId) {
        continue;
      }

      for (const gift of giftItem.children || []) {
        const giftDetails = giftsDetails.find(
          (details) => details.id === gift.id,
        );

        if (!giftDetails?.name) {
          continue;
        }

        gifts.push({
          id: giftDetails.id,
          name: giftDetails.name,
          url: giftDetails.url,
        });
      }
    }

    return gifts;
  }

  static minifyCart() {
    const attributesToKeep = [
      "items",
      "coupons",
      "shippingCountry",
      "defaultCountryCode",
    ];
    const cartItemAttributesToKeep = [
      "id",
      "salesforceId",
      "choice",
      "qty",
      "isFromCoupon",
      "customText",
      "customFont",
      "externalOrderLineId",
      "gifts",
      "productInfos",
    ];
    const choiceAttributesToKeep = ["id", "productId", "salesforceProductId"];

    this.flattenGifts();

    return this.processCartMinification(
      useCartStore().cart,
      attributesToKeep,
      cartItemAttributesToKeep,
      choiceAttributesToKeep,
    );
  }

  static minifyCartForHistory(cart) {
    CartService.flattenGifts(cart);

    const attributesToKeep = [
      "domainId",
      "items",
      "coupons",
      "defaultSubtotal",
      "subtotal",
      "shippingPrice",
      "shippingCountry",
      "total",
    ];
    const cartItemAttributesToKeep = [
      "id",
      "code",
      "choice",
      "qty",
      "salesforceId",
      "externalOrderLineId",
      "name",
      "description",
      "customText",
      "customFont",
      "productInfos",
      "shippingPrice",
      "gifts",
    ];
    const choiceAttributesToKeep = [
      "id",
      "name", // Required for retry mode - CartService.addToCart validates choice.name
      "productId",
      "salesforceProductId",
      "defaultPrice",
      "startingPriceDuration",
      "startingPrice",
      "defaultPrice",
      "unitPrice",
      "bcl",
      "maxTermNumber",
      "renawalTermLength",
      "oldPrice",
      "shippingPriceByCountry",
      "vanguardLabel",
      "vanguardDescription",
      "vanguardDescriptionDetail",
      "description",
      "recordType",
      "format",
      "promotionLabel",
      "freeDays",
    ];

    return this.processCartMinification(
      cart,
      attributesToKeep,
      cartItemAttributesToKeep,
      choiceAttributesToKeep,
    );
  }

  static processCartMinification(
    cart,
    attributesToKeep,
    cartItemAttributesToKeep,
    choiceAttributesToKeep,
  ) {
    const minifiedCart = JSON.parse(JSON.stringify(cart)); // deep copy

    for (const cartKey in minifiedCart) {
      if (!attributesToKeep.includes(cartKey)) {
        delete minifiedCart[cartKey];
      }
    }
    for (const item of minifiedCart.items) {
      for (const productKey in item) {
        if (!cartItemAttributesToKeep.includes(productKey)) {
          delete item[productKey];
        }
        for (const choiceKey in item.choice) {
          if (!choiceAttributesToKeep.includes(choiceKey)) {
            delete item.choice[choiceKey];
          }
        }
      }
    }
    return minifiedCart;
  }

  static calculateDefaultSubtotal(item) {
    let price = 0;
    if (PriceDisplay.shouldShowIntroOffer(item.choice)) {
      price = PriceDisplay.isFreeStartingPrice(item.choice)
        ? 0
        : item.choice.startingPrice;
    } else {
      price = item.choice.defaultPrice ?? item.choice.unitPrice;
    }

    return item.qty * price;
  }

  static generateVirtualMinifyCart(product, choice, coupon) {
    const virtualCart = {
      items: [
        {
          id: product.id,
          salesforceId: product.salesforceId,
          qty: 1,
          choice: {
            id: choice.id,
          },
        },
      ],
      coupons: {},
      shippingCountry: window.globalConfig.defaultCountryCode || "FRA",
    };

    virtualCart.coupons[coupon] = {
      code: coupon,
    };
    return virtualCart;
  }
}
